import { RefObject, useLayoutEffect, useRef } from 'react'

import { Cursor } from '../context/DataGridContext'
import { getGridCursor, toCellId } from '../utils'

export type RecalculatePositionOnNavigationProps = {
    cursor?: Cursor
    firstColumnWidth: number
    headerRef: RefObject<HTMLDivElement | null>
    dataGridRef: RefObject<HTMLDivElement | null>
    footerRef: RefObject<HTMLDivElement | null>
    summaryWidth?: number
}

export function useRecalculatePositionOnNavigation({
    cursor,
    firstColumnWidth,
    headerRef,
    dataGridRef,
    footerRef,
    summaryWidth = 0,
}: RecalculatePositionOnNavigationProps) {
    const previousCursorIdRef = useRef<string | undefined>(undefined)

    useLayoutEffect(() => {
        const cursorId = cursor ? toCellId(cursor) : undefined

        if (!cursorId) {
            previousCursorIdRef.current = undefined
            return
        }

        // Selection consumers can recreate cursor objects; only navigation to another cell should move the viewport.
        if (previousCursorIdRef.current === cursorId) return

        const dataGrid = dataGridRef.current
        const cell = dataGrid ? getGridCursor(dataGrid) : undefined
        if (!cell) return

        const headerRect = headerRef.current?.getBoundingClientRect()
        const footerRect = footerRef.current?.getBoundingClientRect()
        if (!dataGrid) return

        previousCursorIdRef.current = cursorId

        const cellRect = cell.getBoundingClientRect()
        const viewportRect = dataGrid.getBoundingClientRect()
        const leftBoundary = viewportRect.left + dataGrid.clientLeft + firstColumnWidth
        const rightBoundary = viewportRect.left + dataGrid.clientLeft + dataGrid.clientWidth - summaryWidth
        const topBoundary = headerRect?.bottom ?? viewportRect.top + dataGrid.clientTop
        const bottomBoundary = footerRect?.top ?? viewportRect.top + dataGrid.clientTop + dataGrid.clientHeight

        let nextScrollTop = dataGrid.scrollTop
        let nextScrollLeft = dataGrid.scrollLeft

        if (cellRect.bottom > bottomBoundary) {
            nextScrollTop += cellRect.bottom - bottomBoundary
        }

        if (cellRect.right > rightBoundary) {
            nextScrollLeft += cellRect.right - rightBoundary
        }

        if (cellRect.left < leftBoundary) {
            nextScrollLeft -= leftBoundary - cellRect.left + 10
        }

        if (cellRect.top < topBoundary) {
            nextScrollTop -= topBoundary - cellRect.top
        }

        if (nextScrollTop === dataGrid.scrollTop && nextScrollLeft === dataGrid.scrollLeft) return

        // Apply the computed scroll offsets in one go.
        dataGrid.scrollTo({ top: nextScrollTop, left: nextScrollLeft })
    }, [cursor, firstColumnWidth, headerRef, dataGridRef, footerRef, summaryWidth])
}
